import type { DateRange } from "../dateRange";

/** "no Drafts", "1 Draft", "3 Drafts" */
export function countOf(n: number, singular: string, plural = `${singular}s`): string {
  if (n === 0) return `no ${plural}`;
  return `${n} ${n === 1 ? singular : plural}`;
}

/** "is" or "are", to go with `countOf(n, …)` */
export const isAre = (n: number) => (n === 1 ? "is" : "are");

/** "was" or "were", to go with `countOf(n, …)` */
export const wasWere = (n: number) => (n === 1 ? "was" : "were");

export const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/** "an Admin", "a Creator" */
export const aRole = (role: string) => `${/^[aeiou]/i.test(role) ? "an" : "a"} ${role}`;

/** "a, b and c" */
export function listOf(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** How many items a list names before saying "and N more". */
const LISTED = 5;

/** "a, b, c, d, e and 3 more" */
export function listUpTo(items: string[]): string {
  const named = items.slice(0, LISTED);
  const more = items.length - named.length;
  return listOf(more > 0 ? [...named, `${more} more`] : named);
}

/** A Notice or Event title as it's quoted in a reply */
export const quoted = (title: string) => `“${title}”`;

/**
 * A name that matched nothing, said plainly rather than just offered as a
 * list: "There's no Circle called “Grade 3”. Your Circles are: Easter 2025."
 */
export const noneCalled = (kind: string, name: string, names: string[]): string =>
  names.length === 0
    ? `You don't have any ${kind}s yet.`
    : `There's no ${kind} called ${quoted(name)}. Your ${kind}s are: ${listUpTo(names)}.`;

/** Something with a title and when it happens: a scheduled Notice, or an Event. */
export type Dated = { title: string; when: string };

/** "“Sports Day” on Friday, October 9, …" */
export const datedList = (list: Dated[]) =>
  listUpTo(list.map(({ title, when }) => `${quoted(title)} on ${when}`));

/** " today", " in October", or nothing for all time, to finish a sentence. */
export const during = (range: DateRange | undefined) =>
  range && range.start.getTime() > 0 ? ` ${range.label}` : "";

/**
 * A count sentence that says its scope: "In total, …" when there's no range
 * (so `during` stays blank), otherwise `body` as given, capitalised, with its
 * range named at the end by `during`.
 */
export const countSentence = (range: DateRange | undefined, body: string) =>
  range && range.start.getTime() > 0 ? capitalize(body) : `In total, ${body}`;

/** Offers to narrow an all-time count down to this week; nothing for a range already named. */
export const offerThisWeek = (range: DateRange | undefined) =>
  range && range.start.getTime() > 0 ? "" : " Want to see this week?";
